import { jsx as _jsx } from "react/jsx-runtime";
// web/src/components/personas/PersonaAvatar.tsx
// Deterministic SVG avatar seeded from the persona id. Uses DiceBear's
// "personas" style (free, no auth) — each persona always gets the same
// character. Wrapped in a colored ring backdrop tinted from a palette.
//
// DiceBear renders server-side and returns inline SVG, so the avatar is a
// single network fetch per unique seed. Browsers cache aggressively.
const PALETTE = [
    { bg: "bg-emerald-500/15", ring: "ring-emerald-500/40" },
    { bg: "bg-violet-500/15", ring: "ring-violet-500/40" },
    { bg: "bg-amber-500/15", ring: "ring-amber-500/40" },
    { bg: "bg-sky-500/15", ring: "ring-sky-500/40" },
    { bg: "bg-rose-500/15", ring: "ring-rose-500/40" },
    { bg: "bg-teal-500/15", ring: "ring-teal-500/40" },
    { bg: "bg-orange-500/15", ring: "ring-orange-500/40" },
    { bg: "bg-indigo-500/15", ring: "ring-indigo-500/40" },
];
function paletteIndex(seed) {
    let h = 0;
    for (let i = 0; i < seed.length; i++) {
        h = (h * 31 + seed.charCodeAt(i)) | 0;
    }
    return Math.abs(h) % PALETTE.length;
}
// DiceBear's "personas" style is gender-agnostic — without a hint it will
// happily render a feminine name with masculine features and vice versa.
// We bucket the first-name prefix of the seed and bias DiceBear's `hair`
// option list accordingly. The seed still drives the *choice within the
// bucket*, so each persona remains deterministic.
const FEMME_HAIR = ["bobBangs", "bobCut", "curlyBun", "extraLong", "long", "straightBun"];
const MASC_HAIR = ["balding", "buzzcut", "fade", "shortCombover", "shortComboverChops", "mohawk"];
// Covers every persona id in data/education_profiles/. Names are listed in
// lowercase first-name-prefix form. "jordan" stays ambiguous on purpose —
// the name reads both ways, so we let DiceBear pick from the full hair set.
const NAME_GENDER = {
    amara: "f",
    evelyn: "f",
    jasmine: "f",
    linda: "f",
    priya: "f",
    sarah: "f",
    brandon: "m",
    carlos: "m",
    derek: "m",
    michael: "m",
    ryan: "m",
};
function genderFromSeed(seed) {
    const first = seed.toLowerCase().split(/[_\s-]/)[0];
    return NAME_GENDER[first];
}
// Optimistic-looking mouths only. DiceBear's default mouth set includes
// frown / smirk / surprise which read as anxious or skeptical — wrong tone
// for a "pick a profile to explore" picker. The seed still picks one of
// these deterministically.
const OPTIMISTIC_MOUTH = ["smile", "bigSmile"];
function avatarUrl(seed) {
    // DiceBear "personas" — flat illustrated characters. Stable across loads
    // for a given seed. backgroundColor=transparent so the ring color shows.
    const params = new URLSearchParams({ seed, backgroundColor: "transparent" });
    const g = genderFromSeed(seed);
    if (g === "f")
        params.set("hair", FEMME_HAIR.join(","));
    if (g === "m")
        params.set("hair", MASC_HAIR.join(","));
    params.set("mouth", OPTIMISTIC_MOUTH.join(","));
    return `https://api.dicebear.com/9.x/personas/svg?${params.toString()}`;
}
export function PersonaAvatar({ seed, label, size = "md", }) {
    const palette = PALETTE[paletteIndex(seed)];
    const sizing = size === "sm" ? "w-9 h-9" :
        size === "lg" ? "w-14 h-14" :
            "w-11 h-11";
    return (_jsx("span", { className: `${sizing} ${palette.bg} ring-1 ring-inset ${palette.ring} rounded-full overflow-hidden flex items-center justify-center shrink-0`, "aria-label": label, role: "img", children: _jsx("img", { src: avatarUrl(seed), alt: "", className: "w-full h-full", loading: "lazy", draggable: false }) }));
}
